if(!customElements.get('recently-viewed-products')) {
    class RecentlyViewedProducts extends HTMLElement {
        constructor() {
            super();

            this.STORAGE_KEY = "recently_viewed_handles";

            this.swiperContainer = this.querySelector('.swiper-container');
            this.itemsTotal = parseInt(this.swiperContainer?.dataset.itemsTotal) || 0;
        }

        connectedCallback(){
            this.displayProducts();
        }

        async displayProducts() {
            if (!this.swiperContainer) return;

            this.batchAbortController?.abort();
            this.batchAbortController = new AbortController();

            const raw = localStorage.getItem(this.STORAGE_KEY);
            const list = raw ? JSON.parse(raw) : [];

            if (!Array.isArray(list) || list.length === 0){
                this.setAttribute('aria-hidden', 'true');
                return;
            }

            const wrapper = this.swiperContainer.querySelector('.swiper-wrapper');
            if (!wrapper) return;

            wrapper.innerHTML = '';

            const requests = list.slice(0, this.itemsTotal).map((handle) => {
                const url = `/products/${handle}?view=recently-viewed-product-card`;
                return this.getProduct(url, this.batchAbortController.signal);
            });

            await Promise.allSettled(requests);

            this.setAttribute('data-loaded', 'true');

            if (wrapper.children.length > 0) {
                this.initializeSwiper();
            }
        }

        async initializeSwiper(){
            this.slides = this.swiperContainer.querySelectorAll('.swiper-slide:not(.swiper-slide--product-card)');
            if (!this.swiperContainer || this.slides.length <= 1) {
                this.querySelector('.swiper-pagination:not(.swiper-pagination--product-card)')?.classList.add('hidden');
                this.querySelector('.swiper-navigation-wrapper:not(.swiper-navigation-wrapper--product-card)')?.classList.add('hidden');
                return;
            }

            if(typeof Swiper === 'undefined'){
                await new Promise(resolve => {
                    const check = () => {
                        if(typeof Swiper !== 'undefined') resolve();
                        else requestAnimationFrame(check);
                    };
                    check();
                });
            }

            const itemsPerView = parseInt(this.swiperContainer.dataset.itemsPerView);
            const itemsPerViewMobile = parseInt(this.swiperContainer.dataset.itemsPerViewMobile);
            const totalSlides = parseInt(this.swiperContainer.dataset.itemsTotal);
            const autoplay = this.swiperContainer.dataset.autoplay === 'true';
            const speed = parseInt(this.swiperContainer.dataset.speed);
            const loop = this.swiperContainer.dataset.loop === 'true';
            const visibleItemsPerView = (itemsPerView && totalSlides > itemsPerView)? itemsPerView : totalSlides;
            const spaceBetween = parseInt(this.swiperContainer.dataset.gap);

            const breakpoints = {
                0: { slidesPerView: itemsPerViewMobile },
                567: { slidesPerView:  visibleItemsPerView < 2? visibleItemsPerView : 2},
                768: { slidesPerView: visibleItemsPerView < 3? visibleItemsPerView : 3 },
                1200: { slidesPerView: visibleItemsPerView }
            }

            var swiperSettings = {
                init: false,
                initialSlide: 0,
                loop: loop,
                spaceBetween: 0,
                centeredSlides: false,
                speed: 500,
                watchOverflow: true,
                breakpoints: breakpoints,
                observer: true,
                observeSlideChildren: true,
                watchSlidesProgress: true,
                autoHeight: false,
                spaceBetween: spaceBetween,
                on: {
                    init: () => {
                       const paginationBullets = this.querySelectorAll(".swiper-pagination:not(.swiper-pagination--product-card) .swiper-pagination-bullet");
                        if(paginationBullets.length > 0){
                            this.style.setProperty('--pagination-width', `calc(100% / ${paginationBullets.length})`);
                            paginationBullets.forEach((item, index) => {
                                item.setAttribute('data-index', `${index}`);
                            });
                            setTimeout(() => {
                                this.querySelector('.swiper-pagination:not(.swiper-pagination--product-card)')?.classList.add('loaded');
                                this.checkSwiperLock();
                            }, 500);
                        }
                    },
                    slideChange: () => {
                        const activePagination = this.querySelector(".swiper-pagination:not(.swiper-pagination--product-card) .swiper-pagination-bullet-active");
                        if(activePagination){
                            const activePaginationIndex = parseInt(activePagination.dataset.index);
                            this.style.setProperty('--pagination-translate-x', `calc(100% * ${activePaginationIndex})`);
                        }
                    }
                }
            }

            if(this.swiperContainer.querySelector(".swiper-navigation-wrapper:not(.swiper-navigation-wrapper--product-card)")){
                swiperSettings.navigation = {
                    nextEl: this.swiperContainer?.querySelector('.button-next:not(.button-next--product-card)'),
                    prevEl: this.swiperContainer?.querySelector('.button-previous:not(.button-previous--product-card)')
                }
            }

             if(this.querySelector('.swiper-pagination:not(.swiper-pagination--product-card)')){
                swiperSettings.pagination = {
                    el: this.querySelector('.swiper-pagination:not(.swiper-pagination--product-card)')
                }
            }

            if(autoplay && !isNaN(speed)){
                swiperSettings.autoplay = {
                    delay: speed
                }
            }

            this.swiper = new Swiper(this.swiperContainer, swiperSettings);
            this.swiper.init();

            this.swiper.on("resize", this.checkSwiperLock.bind(this));

            this.setupFocusListeners();
        }

        checkSwiperLock(){
            if(!this.querySelector(".swiper-pagination:not(.swiper-pagination--product-card)")) return;
            
            if(this.swiper.isLocked){
                this.querySelector(".swiper-pagination:not(.swiper-pagination--product-card)").classList.add('hidden');
            }else{
                this.querySelector(".swiper-pagination:not(.swiper-pagination--product-card)").classList.remove('hidden');
            }
        }

        setupFocusListeners(){
            this.querySelectorAll(".button-next:not(.swiper-navigation-wrapper--product-card), .button-previous:not(.swiper-navigation-wrapper--product-card)").forEach(button => {
                button.addEventListener('focus', () => {
                    button.classList.add('focused');
                    button.addEventListener('blur', () => {
                        button.classList.remove('focused');
                    }, { once: true });
                });
            });
        }

        getProduct(requestUrl, signal) {
            return fetch(`${requestUrl}&section_id=recently-viewed-product-card`, { signal })
                .then(res => res.text())
                .then(resText => {
                    const html = new DOMParser().parseFromString(resText, 'text/html');

                    const productContainer = this.swiperContainer.querySelector('.swiper-wrapper');
                    const productCard = html.querySelector('.product-card');

                    if (productCard && productContainer) {
                        const slide = document.createElement('div');
                        slide.classList.add('swiper-slide');

                        slide.appendChild(productCard);

                        productContainer.appendChild(slide);
                    }
                })
                .catch(error => {
                    if (error.name === 'AbortError') return;
                    console.error(error);
            });
        }
    }

    customElements.define('recently-viewed-products', RecentlyViewedProducts);
}